// src/app/modules/AccessControl/pages/components/ResidentAccessModal.jsx
import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import * as actions from "../../../Dashbord/_redux/dashboard/Actions";
import { X, Home, Car, ArrowRight, ArrowLeft, Search, ShieldCheck, User } from 'lucide-react';
import './ResidentAccessModal.scss';

export function ResidentAccessModal({ show, onHide, organizationId, onSuccess, getLabel }) {
  const dispatch = useDispatch();

  const [vehiclePlate, setVehiclePlate]       = useState('');
  const [accessCode, setAccessCode]           = useState('');
  const [direction, setDirection]             = useState('entry');
  const [searchTerm, setSearchTerm]           = useState('');
  const [selectedResident, setSelectedResident] = useState(null);
  const [logging, setLogging]                 = useState(false);

  const { residents } = useSelector(
    (state) => ({ residents: state.dashboard.residents || [] }),
    shallowEqual
  );

  useEffect(() => {
    if (show && organizationId) {
      dispatch(actions.fetchAllData('residents', organizationId));
    }
  }, [show, organizationId, dispatch]);

  const formatAccessCode = (input) => {
    const cleaned = input.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    const chunks = cleaned.match(/.{1,3}/g);
    return chunks ? chunks.join('-') : '';
  };

  const handleClose = () => {
    setVehiclePlate('');
    setAccessCode('');
    setDirection('entry');
    setSearchTerm('');
    setSelectedResident(null);
    onHide();
  };

  async function handleLogAccess() {
    setLogging(true);
    try {
      await dispatch(actions.residentAccess({
        organization_id: organizationId,
        vehicle_plate: vehiclePlate || selectedResident?.vehicles?.[0]?.plate_number,
        access_code: accessCode.replace(/-/g, ''),
        direction,
      }, organizationId));
      onSuccess();
      handleClose();
    } catch (e) {
      console.error(e);
    } finally {
      setLogging(false);
    }
  }

  const filteredResidents = residents.filter(r =>
    r.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.unit_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.vehicles?.some(v => v.plate_number?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const canSubmit = !logging && (!!vehiclePlate || !!accessCode || !!selectedResident);

  return (
    <Modal show={show} onHide={handleClose} centered size="md" className="resident-access-modal">
      <div className="modal-content-custom">

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="modal-header-custom">
          <div className="modal-title-custom">
            <div className="title-icon">
              <Home size={16} />
            </div>
            <div className="title-text">
              <h3>{getLabel('residents')} Access Log</h3>
              <p>Record resident entry or exit</p>
            </div>
          </div>
          <button className="btn-close-custom" onClick={handleClose}>
            <X size={15} />
          </button>
        </div>

        {/* ── Body ───────────────────────────────────────────────────────── */}
        <div className="modal-body-custom">

          {/* Direction toggle */}
          <div className="ram-direction">
            <button
              className={`ram-direction__btn ram-direction__btn--entry ${direction === 'entry' ? 'ram-direction__btn--active' : ''}`}
              onClick={() => setDirection('entry')}
            >
              <ArrowRight size={14} />
              Entry
            </button>
            <button
              className={`ram-direction__btn ram-direction__btn--exit ${direction === 'exit' ? 'ram-direction__btn--active' : ''}`}
              onClick={() => setDirection('exit')}
            >
              <ArrowLeft size={14} />
              Exit
            </button>
          </div>

          <div className="ram-form">

            {/* Vehicle plate + resident code */}
            <div className="ram-row">
              <div className="ram-field">
                <label><Car size={12} />Vehicle Plate</label>
                <input
                  type="text"
                  placeholder="ABC-123"
                  value={vehiclePlate}
                  onChange={(e) => { setVehiclePlate(e.target.value.toUpperCase()); setAccessCode(''); setSelectedResident(null); }}
                />
              </div>
              <div className="ram-field">
                <label><ShieldCheck size={12} />Resident Code</label>
                <input
                  type="text"
                  placeholder="XYZ-789"
                  value={accessCode}
                  onChange={(e) => { setAccessCode(formatAccessCode(e.target.value)); setVehiclePlate(''); setSelectedResident(null); }}
                />
              </div>
            </div>

            {/* Divider */}
            <div className="ram-divider"><span>or search resident</span></div>

            {/* Search */}
            <div className="ram-field">
              <label><Search size={12} />Search {getLabel('resident')}</label>
              <input
                type="text"
                placeholder="Name or unit number…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Search results */}
            {searchTerm && !selectedResident && (
              <div className="ram-resident-list">
                {filteredResidents.length > 0 ? (
                  filteredResidents.slice(0, 5).map(resident => (
                    <div
                      key={resident.resident_id}
                      className="ram-resident-item"
                      onClick={() => {
                        setSelectedResident(resident);
                        setSearchTerm('');
                        setVehiclePlate('');
                        setAccessCode('');
                      }}
                    >
                      <div className="ram-resident-item__avatar">
                        <Home size={14} />
                      </div>
                      <div className="ram-resident-item__info">
                        <strong>{resident.name}</strong>
                        <span>
                          {getLabel('unit')} {resident.unit_number}
                          {resident.vehicles?.length > 0 && ` · ${resident.vehicles.length} vehicle${resident.vehicles.length > 1 ? 's' : ''}`}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="ram-no-results">No residents found</div>
                )}
              </div>
            )}

            {/* Selected resident */}
            {selectedResident && (
              <div className="ram-selected">
                <div className="ram-selected__avatar">
                  <User size={14} />
                </div>
                <div className="ram-selected__info">
                  <strong>{selectedResident.name}</strong>
                  <span>{getLabel('unit')} {selectedResident.unit_number}</span>
                </div>
                <button
                  className="ram-selected__clear"
                  onClick={() => setSelectedResident(null)}
                >
                  <X size={13} />
                </button>

                {selectedResident.vehicles?.length > 0 && (
                  <div className="ram-selected__vehicle">
                    <span className="ram-selected__vehicle-label">Select vehicle</span>
                    <select
                      className="ram-field input"
                      onChange={(e) => setVehiclePlate(e.target.value)}
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #EAEAF0', borderRadius: 8, fontSize: 13 }}
                    >
                      <option value="">Choose vehicle…</option>
                      {selectedResident.vehicles.map((v, i) => (
                        <option key={i} value={v.plate_number}>
                          {v.plate_number}{v.vehicle_type ? ` — ${v.vehicle_type}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}

          </div>
        </div>

        {/* ── Footer ─────────────────────────────────────────────────────── */}
        <div className="modal-footer-custom">
          <button className="sety-btn sety-btn-ghost" onClick={handleClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={handleLogAccess}
            disabled={!canSubmit}
          >
            {logging ? 'Logging…' : `Log ${direction === 'entry' ? 'Entry' : 'Exit'}`}
          </button>
        </div>

      </div>
    </Modal>
  );
}